import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import LeaveApplicationForm from './components/LeaveApplicationForm';
import LeaveHistory from './components/LeaveHistory';
import LeaveBalance from './components/LeaveBalance';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import hrService from '../../services/hrService';
import roleService from '../../services/roleService';
import PendingApprovals from './components/PendingApprovals';


const LeaveManagement = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('apply');
  
  // Role-based access state
  const [canApproveLeaves, setCanApproveLeaves] = useState(false);
  const [roleLoading, setRoleLoading] = useState(true);

  // Real state management
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({
    totalAvailable: 0,
    usedDays: 0,
    pendingDays: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleGoBack = () => {
    navigate(-1);
  };

  // Check user role and permissions from database
  useEffect(() => {
    const checkRolePermissions = async () => {
      try {
        setRoleLoading(true);
        
        // Use roleService to check permissions from database
        const canApprove = await roleService?.canApproveLeaves();
        setCanApproveLeaves(canApprove);
        
      } catch (error) {
        console.error('Error checking role permissions:', error);
        setCanApproveLeaves(false);
      } finally {
        setRoleLoading(false);
      }
    };

    if (user) {
      checkRolePermissions();
    }
  }, [user, userProfile]);

  // Load data based on role
  useEffect(() => {
    if (user && !roleLoading) {
      loadLeaveData();
      loadPublicHolidays();
    }
  }, [user, activeTab, roleLoading]);

  const [publicHolidays, setPublicHolidays] = useState([]);

  const loadPublicHolidays = async () => {
    try {
      const holidayService = (await import('../../services/holidayService'))?.default;
      const holidays = await holidayService?.getAllHolidays();
      setPublicHolidays(holidays || []);
    } catch (err) {
      console.error('Error loading public holidays:', err);
    }
  };

  const loadLeaveData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load leave requests - hrService handles role-based filtering
      const requests = await hrService?.getLeaveRequests();
      setLeaveRequests(requests || []);

      // Load leave balance summary for current user using the correct method
      const balanceSummary = await hrService?.getCurrentLeaveBalanceSummary();
      
      setLeaveBalance({
        totalAvailable: balanceSummary?.currentBalance || 0,
        usedDays: balanceSummary?.totalUsed || 0,
        pendingDays: 0 // This would need to be calculated from pending requests if needed
      });

    } catch (err) {
      console.error('Error loading leave data:', err);
      setError(err?.message || 'Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveSubmit = async (leaveData) => {
    try {
      setError('');
      
      // Get current user's employee record
      const employeeRecord = await roleService?.getCurrentUserEmployeeRecord();
      if (!employeeRecord) {
        throw new Error('Employee record not found');
      }

      const newRequest = {
        ...leaveData,
        employeeId: employeeRecord?.id,
        leaveStatus: 'pending'
      };

      const result = await hrService?.createLeaveRequest(newRequest);
      
      // Update local state
      setLeaveRequests(prev => [result, ...prev]);
      
      // Refresh leave balance
      await loadLeaveData();
      
      return { success: true };
    } catch (err) {
      setError(err?.message || 'Failed to submit leave request');
      throw err;
    }
  };

  const handleLeaveApproval = async (requestId, approved, comments = '') => {
    try {
      setError('');
      
      const result = await hrService?.approveLeaveRequest(requestId, approved, comments);
      
      // Update local state
      setLeaveRequests(prev => 
        prev?.map(req => req?.id === requestId ? result : req)
      );
      
      // Reload data to get latest state
      await loadLeaveData();
      
      return { success: true };
    } catch (err) {
      setError(err?.message || 'Failed to process leave request');
      throw err;
    }
  };

  // Role-based tab configuration
  const tabs = canApproveLeaves ? [
    { id: 'apply', label: 'Apply Leave', icon: 'Plus' },
    { id: 'history', label: 'Leave History', icon: 'Clock' },
    { id: 'balance', label: 'Leave Balance', icon: 'Calendar' },
    { id: 'approvals', label: 'Pending Approvals', icon: 'CheckCircle' }
  ] : [
    { id: 'apply', label: 'Apply Leave', icon: 'Plus' },
    { id: 'history', label: 'My Leave History', icon: 'Clock' },
    { id: 'balance', label: 'My Leave Balance', icon: 'Calendar' }
  ];

  if (roleLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main className={`
        transition-smooth pt-16
        ${sidebarCollapsed ? 'ml-16' : 'ml-240'} 
        max-md:ml-0
      `}>
        <div className="p-6">
          {/* Page Header with Back Button */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleGoBack}
                  variant="outline"
                  size="sm"
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">
                    {canApproveLeaves ? 'Leave Management' : 'My Leave Management'}
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    {canApproveLeaves 
                      ? 'Manage employee leave requests and approvals' :'Apply for leave and track your leave balance'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Role-based access notice */}
          {!canApproveLeaves && (
            <div className="mb-6 p-4 bg-info/10 border border-info/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="Info" size={16} color="var(--color-info)" />
                <p className="text-sm font-medium text-info">Employee Leave Portal</p>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                You can apply for leave and view your leave status. Leave approvals are handled by HR/Admin.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="text-error">⚠️</span>
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-smooth
                      ${activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span className="text-foreground">Loading leave data...</span>
              </div>
            </div>
          )}

          {/* Tab Content */}
          {!loading && (
            <div className="space-y-8">
              {activeTab === 'apply' && (
                <LeaveApplicationForm 
                  onSubmit={handleLeaveSubmit}
                  leaveBalance={leaveBalance}
                />
              )}

              {activeTab === 'history' && (
                <LeaveHistory 
                  leaveRequests={leaveRequests}
                  canApprove={false}
                  title={canApproveLeaves ? "All Leave Requests" : "My Leave History"}
                />
              )}

              {activeTab === 'balance' && (
                <LeaveBalance 
                  balance={leaveBalance}
                  isAdmin={canApproveLeaves}
                />
              )}

              {activeTab === 'approvals' && canApproveLeaves && (
                <PendingApprovals 
                  onApprove={handleLeaveApproval}
                  onReject={handleLeaveApproval}
                />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default LeaveManagement;